import { flushSync } from 'react-dom'

import { ButtonRoot } from '@/components/common/Button'
import { useFloating } from '@/components/common/Floating'
import { Icon } from '@/components/icons/Icon'
import { type DropdownOptionGroupType, type DropdownOptionType } from '@/defs/input'

import styles from './InputDropdown.module.scss'

type Props<T extends string = string> = {
  onSelect?: (value: T) => void
  reset?: DropdownOptionType<T>
  value?: T
  values?: T[]
} & (
  | {
      optionGroups: DropdownOptionGroupType<T>[]
    }
  | {
      options: DropdownOptionType<T>[]
    }
)

export const InputDropdown = <T extends string = string>(props: Props<T>) => {
  const { close } = useFloating()
  const {
    onSelect,
    optionGroups: baseGroups,
    reset,
    value,
    values,
  } = 'options' in props ? { ...props, optionGroups: [{ options: props.options }] } : props

  const optionGroups = reset ? [...baseGroups, { options: [reset] }] : baseGroups

  const onClick = (value: T) => {
    flushSync(close)
    onSelect?.(value)
  }

  return (
    <ul className={styles.container}>
      {optionGroups.map((group, i) => (
        <li className={styles.group} key={i}>
          {group.label && <div className={styles.heading}>{group.label}</div>}
          <ul>
            {group.options.map((option, j) => (
              <li key={j}>
                <ButtonRoot
                  as={onSelect ? 'button' : 'div'}
                  className={styles.item}
                  onClick={() => onClick(option.value)}
                >
                  {(option.value === value || values?.includes(option.value)) && option.label !== reset?.label && (
                    <span className={styles.itemIndicator}>
                      <Icon name='check' />
                    </span>
                  )}
                  <div className={styles.itemLabel}>{option.label}</div>
                </ButtonRoot>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  )
}
